import { useState } from 'react';
import { Snackbar, SnackbarContent } from '@mui/material';
import { Map } from 'immutable';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector, useObjectSelector } from '@redux/store';
import { requestActions } from '@redux/slices/functionals/request/requestSlice';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { Typography, TTextType } from '@ts/storybook';
import useEffectDebounced from '@utils/hooks/useEffectDebounced';

interface INotifsProps {
  id?: string;
  notif?: Map<string, any>;
  containerMode?: 'viewport' | 'parent';
  messageFromParent?: TTextType;
  autoHideDuration?: number;
  callbackToClean?: () => void;
}

interface ILevelColorMap {
  warning: string;
  error: string;
}

const TNotifs = (props: INotifsProps) => {
  const { id, notif, containerMode, messageFromParent, autoHideDuration, callbackToClean } = props;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [level, setLevel] = useState('error');
  const [color, setColor] = useState(theme.palette[level]);
  const [message, setMessage] = useState<TTextType>(undefined);

  //const error = useAppSelector(state => state.request.error);
  const { data, loading, error } = useObjectSelector(state => state.request['global']);
  const notification = useAppSelector(state => state.global['notification']);

  //#region Function
  const setNotification = () => {
    if (messageFromParent) {
      setLevel('warning');
      setColor(theme.palette['warning']);
      setMessage(messageFromParent);
      return;
    }
    if (notif) {
      const JSnotif: {
        [x: string]: any;
      } = notif?.toJS();
      setLevel(JSnotif.lvl);
      setColor(JSnotif.lvl ? theme.palette[JSnotif.lvl as keyof ILevelColorMap] : theme.palette['error']);
      setMessage(JSnotif.msg.toString());
    }
    if (error) {
      setLevel('error');
      setColor(theme.palette['error']);
      if (typeof error === 'string') {
        setMessage(error);
      }
      if (error?.message) {
        setMessage(error?.message + '. ' + (error?.suggestion ?? ''));
      }
      //@ts-ignore
      if (error?._gral) {
        //V1 errors
        //@ts-ignore
        setMessage(error?._gral + '( ' + (error?.error ?? '') + ' )');
      }
      return;
    }
    if (notification) {
      setLevel('warning');
      setColor(theme.palette['warning']);

      setMessage(notification);
    }
  };
  const cleanNotification = () => {
    setMessage(undefined);
    if (error) {
      dispatch(requestActions.cleanError());
    }
    if (notification) {
      dispatch(globalActions.cleanNotification());
    }
    callbackToClean?.();
  };
  //#endregion

  useEffectDebounced(() => {
    setNotification();
    setTimeout(() => {
      cleanNotification();
    }, autoHideDuration ?? 5000);
  }, [notif, error, notification, messageFromParent]);

  if (!notif && !error && !notification && !messageFromParent) {
    return null;
  }

  const inParent = containerMode === 'parent';
  return (
    <Snackbar
      key={id}
      data-testid={id}
      open={!!message}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      style={inParent ? { position: 'absolute' } : undefined}
      sx={
        inParent
          ? {
              position: 'absolute',
              left: '50%',
              bottom: 8,
              transform: 'translateX(-50%)',
              zIndex: theme => theme.zIndex.snackbar,
            }
          : undefined
      }
    >
      <SnackbarContent
        sx={{
          bgcolor: color.main,
          color: color.contrastText,
        }}
        message={<Typography text={messageFromParent ?? message} />}
      />
    </Snackbar>
  );
};

export default TNotifs;
